import { useState, useEffect } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { Popover } from "@headlessui/react"
import { Sun, Moon, LogOut, LayoutDashboard, Menu, X, ChevronDown, Wrench } from "lucide-react"
import { useLang } from "../context/LangContext"
import { useAuth } from "../context/AuthContext"

const NAV = {
  en: {
    about: "About", alliances: "Alliances", tools: "Tools", pricing: "Pricing",
    faqs: "FAQs", contact: "Contact",
    toolItems: [
      { label: "Compound Interest", to: "/compound-interest" },
      { label: "Time Tracker", to: "/app/time-tracker" },
      { label: "Coin Market", to: "/coin-market-tracker" },
      { label: "Economic Calendar", to: "/economic-calendar" },
    ],
    login: "Sign in", dashboard: "Dashboard", logout: "Log out",
  },
  es: {
    about: "Nosotros", alliances: "Aliados", tools: "Herramientas", pricing: "Planes",
    faqs: "Preguntas", contact: "Contacto",
    toolItems: [
      { label: "Interés Compuesto", to: "/compound-interest" },
      { label: "Time Tracker", to: "/app/time-tracker" },
      { label: "Mercado Cripto", to: "/coin-market-tracker" },
      { label: "Calendario Económico", to: "/economic-calendar" },
    ],
    login: "Iniciar sesión", dashboard: "Dashboard", logout: "Cerrar sesión",
  },
}

export default function Header() {
  const { lang, setLang, dark, toggleDark } = useLang()
  const t = NAV[lang]
  const { user, signOut } = useAuth()
  const userEmail = user?.email ?? null
  const [mobileOpen, setMobileOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  const handleLogout = async () => {
    await signOut()
    navigate("/")
  }

  const isActive = (p: string) => location.pathname === p || location.pathname.startsWith(p + "/")

  const navLink = (path: string, label: string) => (
    <Link key={path} to={path} className={`text-sm transition-colors ${
      isActive(path)
        ? "text-[#2563EB] dark:text-[#60a5fa] font-medium"
        : "text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white"
    }`}>{label}</Link>
  )

  // Icon button — clearly visible in both modes
  const iconBtn = "p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 hover:bg-gray-100 dark:hover:bg-neutral-700 transition text-gray-700 dark:text-gray-200 shadow-sm"

  return (
    <header className="sticky top-0 z-50 bg-white dark:bg-neutral-950 border-b border-gray-200 dark:border-gray-800 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14 gap-4">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0 hover:opacity-80 transition">
          <img src="/logo.png" alt="uCloud360" className="h-7 w-auto" />
          <span className="font-semibold text-sm hidden sm:block text-black dark:text-white">uCloud360</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-5 ml-4 flex-1">
          {navLink("/about", t.about)}
          {navLink("/alliances", t.alliances)}
          {navLink("/faqs", t.faqs)}
          {navLink("/contact", t.contact)}

          <Popover className="relative">
            <Popover.Button className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white transition">
              <Wrench className="h-3.5 w-3.5" />{t.tools}<ChevronDown className="h-3 w-3" />
            </Popover.Button>
            <Popover.Panel className="absolute top-full left-0 mt-2 w-52 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1 z-50">
              {t.toolItems.map(item => (
                <Link key={item.to} to={item.to} className="flex px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                  {item.label}
                </Link>
              ))}
            </Popover.Panel>
          </Popover>

          {navLink("/pricing", t.pricing)}
        </nav>

        {/* Controls */}
        <div className="flex items-center gap-2">
          {/* Lang toggle — shows current lang, click to switch */}
          <button
            onClick={() => setLang(lang === "en" ? "es" : "en")}
            className={`${iconBtn} min-w-[36px] text-xs font-semibold tracking-wide`}
            title={lang === "en" ? "Cambiar a Español" : "Switch to English"}
          >
            {lang === "en" ? "ES" : "EN"}
          </button>

          {/* Dark toggle */}
          <button onClick={toggleDark} className={iconBtn} title={dark ? "Light mode" : "Dark mode"}>
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          {/* Auth menu */}
          <Popover className="relative">
            <Popover.Button className="flex items-center gap-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-neutral-700 transition text-black dark:text-white shadow-sm max-w-[160px]">
              <span className="truncate">{userEmail ? userEmail.split("@")[0] : t.login}</span>
            </Popover.Button>
            <Popover.Panel className="absolute right-0 z-50 mt-2 w-52 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-2">
              {!userEmail ? (
                <button onClick={() => navigate("/login")} className="w-full text-left text-sm px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 transition">
                  {t.login}
                </button>
              ) : (
                <>
                  <div className="px-3 py-2 text-xs text-gray-400 truncate border-b border-gray-100 dark:border-gray-800 mb-1">{userEmail}</div>
                  <button onClick={() => navigate("/app/dashboard")} className="w-full text-left text-sm flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 transition">
                    <LayoutDashboard className="h-4 w-4" />{t.dashboard}
                  </button>
                  <button onClick={handleLogout} className="w-full text-left text-sm flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 transition">
                    <LogOut className="h-4 w-4" />{t.logout}
                  </button>
                </>
              )}
            </Popover.Panel>
          </Popover>

          {/* Mobile hamburger */}
          <button className={`${iconBtn} md:hidden`} onClick={() => setMobileOpen(o => !o)}>
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-950 px-4 py-4 space-y-1">
          <Link to="/about" className="block py-2.5 text-sm text-gray-700 dark:text-gray-200">{t.about}</Link>
          <Link to="/alliances" className="block py-2.5 text-sm text-gray-700 dark:text-gray-200">{t.alliances}</Link>
          <Link to="/pricing" className="block py-2.5 text-sm text-gray-700 dark:text-gray-200">{t.pricing}</Link>
          <Link to="/faqs" className="block py-2.5 text-sm text-gray-700 dark:text-gray-200">{t.faqs}</Link>
          <Link to="/contact" className="block py-2.5 text-sm text-gray-700 dark:text-gray-200">{t.contact}</Link>
          <div className="pt-1 border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-400 py-2">{t.tools}</p>
            {t.toolItems.map(item => (
              <Link key={item.to} to={item.to} className="block py-2 pl-3 text-sm text-gray-600 dark:text-gray-300">{item.label}</Link>
            ))}
          </div>
        </div>
      )}
    </header>
  )
}
